import { Link } from 'react-router-dom'
import { Play, Check, Clock, LucideIcon } from 'lucide-react'
import { motion } from 'framer-motion'

interface EpisodeCardWithIconProps {
  moduleId: number
  moduleOrder: number
  episodeNumber: number
  title: string
  icon: LucideIcon
  thumbnailUrl?: string | null
  completed?: boolean
  progressPercent?: number
  status?: string
}

const EpisodeCardWithIcon = ({
  moduleId,
  moduleOrder,
  episodeNumber,
  title,
  icon: Icon,
  thumbnailUrl,
  completed,
  progressPercent,
  status,
}: EpisodeCardWithIconProps) => {
  const hasProgress = completed || (progressPercent ?? 0) > 0
  const isComingSoon = status === 'coming_soon'

  const cardContent = (
    <motion.div
      className={`aspect-video bg-black/90 rounded-lg overflow-hidden relative border transition-all duration-300 ${
        isComingSoon
          ? 'border-border/50 opacity-60 cursor-default'
          : 'border-border hover:border-primary/50'
      }`}
      whileHover={isComingSoon ? {} : { scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {thumbnailUrl ? (
        <>
          <img
            src={thumbnailUrl}
            alt={title}
            className={`absolute inset-0 w-full h-full object-cover ${isComingSoon ? 'grayscale' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300 pointer-events-none" />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ scale: 1 }}
            whileHover={isComingSoon ? {} : { scale: 1.1 }}
            transition={{ duration: 0.3 }}
          >
            <Icon
              className={`w-24 h-24 transition-colors duration-300 ${
                isComingSoon
                  ? 'text-muted-foreground/40'
                  : 'text-primary/80 group-hover:text-primary'
              }`}
              strokeWidth={1.5}
            />
          </motion.div>
        </div>
      )}

      {/* Completed badge - top right */}
      {completed && !isComingSoon && (
        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-lg z-10">
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </div>
      )}

      {isComingSoon ? (
        /* Tag "Em Breve" no lugar do play */
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-muted/90 text-muted-foreground text-xs font-medium px-3 py-1.5 rounded-full">
          <Clock className="w-3.5 h-3.5" />
          Em Breve
        </div>
      ) : (
        /* Play button bottom right */
        <motion.div
          className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/50"
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.2 }}
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      )}

      {/* Progress bar at bottom */}
      {hasProgress && !isComingSoon && (
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/40 z-10">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${Math.min(completed ? 100 : (progressPercent ?? 0), 100)}%` }}
          />
        </div>
      )}

      {/* Hover overlay */}
      {!isComingSoon && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}
    </motion.div>
  )

  return isComingSoon ? (
    <div className="relative">
      {cardContent}
      <div className="mt-3 px-1">
        <h4 className="text-sm font-medium text-muted-foreground/60 line-clamp-2">
          Ep {moduleOrder}.{episodeNumber} - {title}
        </h4>
      </div>
    </div>
  ) : (
    <Link to={`/episode/${moduleId}/${episodeNumber}`} className="group relative">
      {cardContent}
      <div className="mt-3 px-1">
        <h4 className="text-sm font-medium text-foreground/90 group-hover:text-primary transition-colors duration-200 line-clamp-2">
          Ep {moduleOrder}.{episodeNumber} - {title}
        </h4>
      </div>
    </Link>
  )
}

export default EpisodeCardWithIcon
